import React, { useState } from 'react';
import _ from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl';
import 'draft-js/dist/Draft.css';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';

import {
  languageFieldMap,
  getDisplaySelectedFieldName
} from '../../../i18n';

import useGlobalStatus from '../../../hook/useGlobalStatus';

import { HelpOutlineIcon, EditOutlinedIcon, HighlightOffIcon, ErrorOutlineIcon } from '../../../constants/images';
import theme from '../../../theme';

import SortFields from '../SortFields';

const GoogleFieldsSelected = (props) => {
  const {
    allfields,
    excelSheetSelected,
    handleDeleteFields,
    reportLanguage,
    currentSheetName,
    allDisplayFields,
    updateSortFields,
    setUpdateSortFields,
    currentSheetSelectFields,
    setCurrentSheetSelectFields,
    currenOptionFields,
    setCurrenOptionFields,
    setSelectedGeneralFields,
    setSelectedConversionsFields,
    setSelectedCustomFields,
    fieldsBlockHeightS,
    mediaMainColor,
    handleModalDetail,
    handleUpdateSortFields
  } = props;

  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState(null);
  const popOpen = Boolean(anchorEl);

  const closeOptionFieldsName = () => {
    setAnchorEl(null);

    if (currenOptionFields.name === '') return;

    const languageField = languageFieldMap[reportLanguage];

    const updateFields = (fields) => {
      return fields.map((field) => {
        if (field.expression === currenOptionFields.expression) {
          const updatedField = { ...field };
          if (languageField) {
            updatedField[languageField] = currenOptionFields[languageField];
            updatedField.name = currenOptionFields.name;
          }
          return updatedField;
        }
        return field;
      });
    };

    setCurrentSheetSelectFields((prev) => {
      const updated = updateFields(prev);
      return updated.sort((a, b) => {
        if (a.indexNumber === null) return 1;
        if (b.indexNumber === null) return -1;
        return a.indexNumber - b.indexNumber;
      });
    });

    setSelectedGeneralFields((prev) => updateFields(prev));
    setSelectedConversionsFields((prev) => updateFields(prev));
    setSelectedCustomFields((prev) => updateFields(prev));
  };

  const changeCurrentOptionFields = (value) => {
    const nameKey = languageFieldMap[reportLanguage];

    setCurrenOptionFields(prev => ({
      ...prev,
      name: value,
      ...(nameKey ? { [nameKey]: value } : {}),
    }));
  }

  const getOriginalFieldName = (allOptionFields, name) => {
    const target = _.flatMap(allOptionFields, item => {
      return _(item.child).filter({ expression: name }).value()
    })[0];

    if (target.type !== 'CUSTOM') {
      return <FormattedMessage id={`google.field.${target.expression}`} />
    }
    return target.name;
  }

  return (
    <Box sx={{ flex: 1.6 }} >
      <Box sx={{ padding: '16px 16px 4px' }} >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            paddingBottom: '4px',
            borderBottom: `1px solid ${theme.palette.grey.w200}`
          }}
        >
          <Typography>
            <FormattedMessage id="createData.field.selectedField" />
            <Tooltip
              title={
                <Box sx={{ lineHeight: 1.5, padding: '4px' }}>
                  <Typography variant='body_xs'><FormattedMessage id="createData.field.selectedFieldTip1" /></Typography><br />
                  <Typography variant='body_xs'><FormattedMessage id="createData.field.selectedFieldTip2" /></Typography><br />
                  <Typography variant='body_xs'><FormattedMessage id="createData.field.selectedFieldTip3" /></Typography>
                </Box>
              }
              placement="bottom-start"
            >
              <HelpOutlineIcon
                variant="dark"
                sx={{
                  top: '2px',
                  width: '16px',
                  height: '16px',
                  marginLeft: '4px',
                }}
              />
            </Tooltip>
          </Typography>

          <SortFields {...props} media="GOOGLE" />
        </Box>
      </Box>

      <Box
        sx={{
          height: fieldsBlockHeightS,
          overflow: 'auto',
          padding: '8px 16px 0',
          transition: 'all .5s',
        }}
      >
        {
          currentSheetName === "" ?
            (<Box
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: theme.palette.grey.w300,
              }}
            >
              <ErrorOutlineIcon sx={{ right: '4px' }} />
              <FormattedMessage id="createData.tab.selectTip" />
            </Box>) : ""
        }
        {
          currentSheetName && currentSheetSelectFields.length > 0 &&
          currentSheetSelectFields.map(el => {
            return (
              <Box
                key={el.expression}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                  padding: '4px 8px 4px 4px',
                  border: `1px solid ${theme.palette.grey.w200}`,
                  borderRadius: '4px',
                }}
              >
                {
                  el.expression === 'metrics.impressions' || el.expression === 'metrics.cost_micros' ?
                    (<HighlightOffIcon
                      variant="dark"
                      sx={{
                        color: theme.palette.grey.w300,
                        marginRight: '4px',
                      }}
                    />
                    ) : (
                      <HighlightOffIcon
                        variant="dark"
                        sx={{
                          marginRight: '4px',
                          '&:hover': {
                            color: mediaMainColor,
                            cursor: 'pointer'
                          }
                        }}
                        onClick={(e) => handleDeleteFields(e, el, 'deleteSheet')}
                      />
                    )
                }
                <Typography
                  sx={{
                    flex: 1,
                    marginRight: '8px',
                    borderRight: `1px solid ${theme.palette.grey.w200}`,
                  }}
                >
                  {getDisplaySelectedFieldName(el, reportLanguage)}
                </Typography>
                {
                  // 自訂轉換欄
                  el.cid !== null && el.type === 'CUSTOM' ?
                    (<EditOutlinedIcon
                      variant="dark"
                      sx={{
                        color: theme.palette.grey.w300,
                      }}
                    />) : (
                      <EditOutlinedIcon
                        variant="dark"
                        sx={{
                          '&:hover': {
                            color: mediaMainColor,
                            cursor: 'pointer'
                          }
                        }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setCurrenOptionFields(el);
                        }}
                      />
                    )
                }
              </Box>
            )
          })
        }
        {/* 修改欄位名 */}
        {
          currenOptionFields &&
          <Popover
            open={popOpen}
            anchorEl={anchorEl}
            onClose={closeOptionFieldsName}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box
              sx={{
                width: '350px',
                padding: '16px'
              }}
            >
              <Typography variant="caption">
                <FormattedMessage id="createData.field.enterFieldName" />
              </Typography>
              <TextField
                variant="standard"
                id="customOptionalFieldName"
                name="customOptionalFieldName"
                value={getDisplaySelectedFieldName(currenOptionFields, reportLanguage)}
                onChange={(e) => changeCurrentOptionFields(e.target.value)}
                fullWidth
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.grey.w600
                }}
              >
                <FormattedMessage id="createData.field.sourceField" />{getOriginalFieldName(allDisplayFields, currenOptionFields.expression)}
              </Typography>
            </Box>

          </Popover>
        }
      </Box>
    </Box>
  )
}

export default GoogleFieldsSelected;